import React, { useState, useEffect, useRef, useMemo } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { is, isEmpty } from 'ramda';
import { useForm } from 'react-hook-form';
import { klona } from 'klona';
import { wrap } from 'object-path-immutable';

// store
import { storeSet, useStoreValue } from '../../store';

// api
import AmendmentsService from '../../service/amendments-service';
import ApplicationService from '../../service/application-service';

// tools
import set404FromErrorResponse from '../../helpers/set404FromErrorResponse';
import getBandoLabel from '../../helpers/getBandoLabel';
import getDateFromISOstring from '../../helpers/getDateFromISOstring';
import getEmailTemplateForSoccorso from '../../helpers/getStrippedHtmlBodyTags';

// components
import { Button } from 'primereact/button';
import BlockingOverlay from '../../components/BlockingOverlay';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import FormField from '../../components/FormField';
import SoccorsoComunications from '../SoccorsoEditPreInstructor/components/SoccorsoComunications';
import { Editor } from 'primereact/editor';

const APP_HUB_ID = process.env.REACT_APP_HUB_ID;

const SoccorsoEditBeneficiario = () => {
    const isAsyncRequest = useStoreValue('isAsyncRequest');
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [dataAppl, setDataAppl] = useState({});
    const [isVisibleEmailDialog, setIsVisibleEmailDialog] = useState(false);
    const toast = useRef(null);
    const [formInitialData, setFormInitialData] = useState({});
    const [answersModal, setAnswersModal] = useState(false);
    const [flaggedAnswers, setFlaggedAnswers] = useState([]);
    const [answerValues, setAnswerValues] = useState({});
    const [submittingIntegration, setSubmittingIntegration] = useState(false);
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        register,
        trigger,
        getValues
    } = useForm({
        defaultValues: useMemo(() => {
            return formInitialData;
        }, [formInitialData]), mode: 'onChange'
    });

    const goToArchivePage = () => {
        // /domande per il ruolo Fornitore e' mappata su PageNotFound: tornare
        // indietro da qui portava a un 404. L'elenco del fornitore e' /imieimoduli.
        navigate(`/imieimoduli`);
    }

    useEffect(() => {
        const applId = parseInt(id);
        if (!applId) return;
        ApplicationService.getAnswersReview(applId, (resp) => {
            if (resp.status === 'SUCCESS' && Array.isArray(resp.data)) {
                const flagged = resp.data.filter(r => r.daIntegrare);
                setFlaggedAnswers(flagged);
                const init = {};
                flagged.forEach(f => { init[f.fieldId] = f.value || ''; });
                setAnswerValues(init);
            }
        }, () => {});
    }, [id]);

    const apriModaleIntegrazioni = () => setAnswersModal(true);

    const inviaIntegrazione = () => {
        setSubmittingIntegration(true);
        const answers = flaggedAnswers.map(f => ({ fieldId: f.fieldId, value: answerValues[f.fieldId] != null ? answerValues[f.fieldId] : '' }));
        ApplicationService.submitIntegrationAnswers(parseInt(id), { answers }, (resp) => {
            setSubmittingIntegration(false);
            if (resp.status === 'SUCCESS') {
                setAnswersModal(false);
                if (toast.current) toast.current.show({ severity: 'success', summary: '', detail: __('Integrazione inviata correttamente.', 'htr') });
                setTimeout(() => navigate('/imieimoduli'), 1200);
            } else if (toast.current) {
                toast.current.show({ severity: 'error', summary: '', detail: resp.message || __('Invio non riuscito.', 'htr') });
            }
        }, (err) => {
            setSubmittingIntegration(false);
            if (toast.current) toast.current.show({ severity: 'error', summary: '', detail: (err && err.message) || __('Invio non riuscito.', 'htr') });
        });
    };

    useEffect(() => {
        const parsedId = parseInt(id);
        const entityId = !isNaN(parsedId) ? parsedId : 0;

        AmendmentsService.getSoccorsoByApplId(entityId, getCallback, errGetCallback, [
            ['statuses', 'AWAITING, RESPONSE_RECEIVED']
        ]);
    }, [id]);

    const getCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (data.data.length) {
                const amendmentObj = data.data[0];
                setData(getFormattedData(amendmentObj));
                let formDataInitial = amendmentObj.applicationFormFields.reduce((acc, cur) => {
                    if (cur.fieldValue) {
                        acc[cur.fieldId] = cur.fieldValue;
                    }
                    return acc;
                }, {});
                formDataInitial = {
                    ...formDataInitial,
                    amendmentDocuments: amendmentObj.amendmentDocuments
                }
                setFormInitialData(formDataInitial);
                storeSet('unsetAsyncRequest');
            } else {
                ApplicationService.getApplication(id, getApplCallback, errGetCallback)
            }
        }
    }

    const errGetCallback = (data) => {
        if (toast.current && data.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message
            });
        }
        set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    const getApplCallback = (data) => {
        if (data.status === 'SUCCESS') {
            setDataAppl(getFormattedData(data.data));
        }
        storeSet('unsetAsyncRequest');
    }

    const getFormattedData = (data) => {
        data.evaluationEndDate = is(String, data.evaluationEndDate) ? new Date(data.evaluationEndDate) : (data.evaluationEndDate ? data.evaluationEndDate : '');
        data.startDate = is(String, data.startDate) ? new Date(data.startDate) : (data.startDate ? data.startDate : '');
        data.expirationDate = is(String, data.expirationDate) ? new Date(data.expirationDate) : (data.expirationDate ? data.expirationDate : '');
        return data;
    };

    const isSpecialAmendmentValidationFailed = () => {
        if (data.amendmentType === 'SPECIAL') {
            const formValues = getValues();
            const amendmentDocuments = formValues.amendmentDocuments;
            return !amendmentDocuments || amendmentDocuments.length === 0;
        }
        return false;
    };

    const onSubmit = () => {
    };

    const doUpdateAmendment = (doClose = false) => {
        trigger();
        let formValues = klona(getValues());
        const newFormValues = Object.keys(formValues)
            .filter(v => v !== 'amendmentDocuments')
            .reduce((acc, cur) => {
                let fieldVal = formValues[cur];

                fieldVal = isEmpty(fieldVal) ? null : fieldVal;
                fieldVal = is(Array, fieldVal) ? fieldVal.map(o => o.id).join(',') : null;

                acc.push({
                    'fieldId': cur,
                    'fieldValue': fieldVal
                });
                return acc;
            }, []);
        const newAmendDocs = formValues.amendmentDocuments
            ? formValues.amendmentDocuments.map(o => o.id).join(',')
            : '';

        const submitData = {
            applicationFormFields: newFormValues,
            amendmentDocuments: newAmendDocs,
            amendmentNotes: data.amendmentNotes
        }
        const amendmentId = data.id;

        storeSet('setAsyncRequest');

        AmendmentsService.updateSoccorso(
            amendmentId,
            submitData,
            (resp) => updateAmendmentCallback(resp, doClose),
            errUpdateAmendmentCallback
        );
    }

    const doUpdateAmendmentAndCompleteTask = () => {
        doUpdateAmendment(true);
    }

    const updateAmendmentCallback = (data, doClose = false) => {
        if (data.status === 'SUCCESS') {
            setData(getFormattedData(data.data));

            if (doClose) {
                AmendmentsService.updateStatusSoccorso(data.data.id, updateAmendmentCallback, errUpdateAmendmentCallback, [
                    ['status', 'RESPONSE_RECEIVED']
                ]);
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: '',
                        detail: data.message
                    });
                }
                let formDataInitial = data.data.applicationFormFields.reduce((acc, cur) => {
                    if (cur.fieldValue) {
                        acc[cur.fieldId] = cur.fieldValue;
                    }
                    return acc;
                }, {});
                formDataInitial = {
                    ...formDataInitial,
                    amendmentDocuments: data.data.amendmentDocuments
                }
                setFormInitialData(formDataInitial);
            }
        }
        storeSet('unsetAsyncRequest');
    }

    const errUpdateAmendmentCallback = (data) => {
        if (toast.current && data.message) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: data.message
            });
        }
        //set404FromErrorResponse(data);
        storeSet('unsetAsyncRequest');
    }

    const updateNewAmendmentData = (value, path) => {
        const newData = wrap(data).set(path, value).value();
        setData(newData);
    }

    const renderHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const header = renderHeader();

    useEffect(() => {
        if (formInitialData) {
            //reset();
            Object.keys(formInitialData).map(k => setValue(k, formInitialData[k]));
            trigger();
        }
    }, [formInitialData]);

    return (
        <div className="appPage">
            <div className="appPage__pageHeader">
                {data.id
                    ? <h1>
                        {sprintf(__('Richiesta di integrazioni per la domanda #%s', 'htr'), id)}
                    </h1> : null}
                {dataAppl.id
                    ? <h1>
                        {sprintf(__('Dettagli: domanda #%s', 'htr'), dataAppl.id)}
                    </h1> : null}
            </div>

            <div className="appPage__spacer"></div>
            <Toast ref={toast}/>
            <BlockingOverlay shouldDisplay={isAsyncRequest}/>

            <div className="appPageSection__row">
                <Button
                    type="button"
                    outlined
                    onClick={goToArchivePage}
                    label={__('Indietro', 'htr')}
                    icon="pi pi-arrow-left" iconPos="left"/>
            </div>

            <div className="appPage__spacer"></div>

            <div className="appPage__content">
                {data.id
                    ? <div className="appPageSection__withBorder columns">
                        <p className="appPageSection__pMeta">
                            <span>{__('ID domanda', 'htr')}</span>
                            <span>{data.applicationId}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Bando', 'htr')}</span>
                            <span>{data.callName}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Referente Aziendale', 'htr')}</span>
                            <span>{data.beneficiaryName}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Azienda Beneficiaria', 'htr')}</span>
                            <span></span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Inizio', 'htr')}</span>
                            <span>{getDateFromISOstring(data.startDate)}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Scadenza', 'htr')}</span>
                            <span>{getDateFromISOstring(data.expirationDate)}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Stato', 'htr')}</span>
                            <span>{getBandoLabel(data.status)}</span>
                        </p>
                    </div> : null}
                {dataAppl.id
                    ? <div className="appPageSection__withBorder columns">
                        <p className="appPageSection__pMeta">
                            <span>{__('ID domanda', 'htr')}</span>
                            <span>{dataAppl.id}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Bando', 'htr')}</span>
                            <span>{dataAppl.callTitle}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Azienda Beneficiaria', 'htr')}</span>
                            <span>{dataAppl.companyName}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Inviato', 'htr')}</span>
                            <span>{getDateFromISOstring(dataAppl.submissionDate)}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Protocolo', 'htr')}</span>
                            <span>{dataAppl.protocolNumber}</span>
                        </p>
                        <p className="appPageSection__pMeta">
                            <span>{__('Stato', 'htr')}</span>
                            <span>{getBandoLabel(dataAppl.status)}</span>
                        </p>
                    </div> : null}

                {data.id
                    ? <div className="appPageSection__emailTemplate">
                        <h2>{__('Dettagli richiesta', 'htr')}</h2>
                        <h3>{__('Note e spiegazioni', 'htr')}</h3>
                        {getEmailTemplateForSoccorso(data.emailTemplate, data.note)}
                        <div className="appPageSection__actions" style={{ marginTop: '1rem' }}>
                            {flaggedAnswers.length > 0
                                ? <Button
                                    type="button"
                                    onClick={apriModaleIntegrazioni}
                                    label={__('Correggi le risposte del questionario', 'htr')}
                                    icon="pi pi-list"
                                    iconPos="right"/>
                                : null}
                            <Button
                                type="button"
                                outlined
                                onClick={() => navigate(`/imieimoduli/${id}`)}
                                label={__('Apri il Modulo di Valutazione ESG', 'htr')}
                                icon="pi pi-pencil"
                                iconPos="right"/>
                        </div>
                    </div> : null}

                {data.id
                    ? <div className="appPageSection">
                        <h2>{__('Comunicazioni', 'htr')}</h2>
                        <SoccorsoComunications amendmentId={data.id} soccorsoStatus={data.status}/>
                    </div> : null}

                {data.id && data.amendmentInitialDocument && is(Array, data.amendmentInitialDocument)
                    ? <div className="appPageSection">
                        <h3>{__('Documenti da PEC', 'htr')}</h3>
                        <ol className="appPageSection__list">
                            {data.amendmentInitialDocument.map((o, i) => <li key={o.fieldId}>
                                <a href={o.filePath} target="_blank" rel="noreferrer">{o.name}</a>
                            </li>)}
                        </ol>
                    </div> : null}

                {data.id
                    ? <div className="appPageSection">
                        <h3>{__('Documenti richiesti', 'htr')}</h3>
                        <form className="appForm" onSubmit={handleSubmit(onSubmit)}>
                            {data.formFields
                                ? data.formFields.map((o, i) => {
                                    // HTR: caricamento documenti richiesti avviene dalla scheda documenti del fornitore,
                                    // non da questa pagina. Riquadro fileupload disattivato per fieldId "document-*".
                                    if (o.fieldId && o.fieldId.indexOf("document-") === 0) return null;
                                    return <FormField
                                        key={o.fieldId}
                                        disabled={data.status !== 'AWAITING'}
                                        type="fileupload"
                                        setDataFn={setValue}
                                        saveFormCallback={doUpdateAmendment}
                                        fieldName={o.fieldId}
                                        label={o.label}
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        defaultValue={formInitialData[o.fieldId] ? formInitialData[o.fieldId] : []}
                                        accept={[]}
                                        source="AMENDMENT"
                                        sourceId={data.id}
                                        multiple={true}
                                    />
                                }) : null}
                        </form>
                        <ol className="appPageSection__list">
                            {data.formFields
                                ? data.formFields.map((o, i) => <li key={o.fieldId}
                                                                    style={{ flexDirection: 'row' }}>
                                    <span>{o.label}{o.note ? ' \u2014 ' + o.note : ''}</span>
                                </li>) : null}
                        </ol>
                    </div> : null}

                {data.id
                    ? <div className="appPageSection">
                        <h2>{__('Documenti aggiuntivi', 'htr')}</h2>
                        <div className="appPageSection">
                            <h3>{__('Notes', 'htr')}</h3>
                            <div style={{ marginBottom: '30px', width: '100%', position: 'relative' }} translate="no">
                                <BlockingOverlay shouldDisplay={data.status !== 'AWAITING'}/>
                                <Editor
                                    value={data.amendmentNotes}
                                    readOnly={data.status !== 'AWAITING'}
                                    placeholder={__('Digita qui il messagio', 'htr')}
                                    headerTemplate={header}
                                    onTextChange={(e) => updateNewAmendmentData(
                                        e.htmlValue,
                                        'amendmentNotes'
                                    )}
                                    style={{ height: 80 * 3, width: '100%' }}
                                />
                            </div>
                            <FormField
                                type="fileupload"
                                disabled={data.status !== 'AWAITING'}
                                setDataFn={setValue}
                                saveFormCallback={doUpdateAmendment}
                                fieldName="amendmentDocuments"
                                label={__('I file', 'htr')}
                                control={control}
                                register={register}
                                errors={errors}
                                defaultValue={formInitialData.amendmentDocuments ? formInitialData.amendmentDocuments : []}
                                accept={[]}
                                config={{
                                    required: true
                                }}
                                source="amendment"
                                sourceId={data.id}
                                multiple={true}
                            />
                        </div>
                    </div> : null}

                <div className="appPageSection">
                    <div className="appPageSection__actions">
                        {data.id
                            ? <Button
                                type="button"
                                disabled={isAsyncRequest || data.status !== 'AWAITING' || isSpecialAmendmentValidationFailed()}
                                onClick={doUpdateAmendmentAndCompleteTask}
                                label={__('Invia documenti', 'htr')}
                                icon="pi pi-save" iconPos="right"/> : null}
                        {data.id
                            ? <Button
                                type="button"
                                outlined
                                disabled={isAsyncRequest || data.status !== 'AWAITING'}
                                onClick={() => doUpdateAmendment()}
                                label={__('Salva bozza', 'htr')}
                                icon="pi pi-save" iconPos="right"/> : null}
                        <Button
                            type="button"
                            outlined
                            onClick={goToArchivePage}
                            label={__('Indietro', 'htr')}
                            icon="pi pi-arrow-left" iconPos="left"/>
                    </div>
                </div>
            </div>

            <Dialog
                header={__('Correggi le risposte richieste', 'htr')}
                visible={answersModal}
                style={{ width: '46rem', maxWidth: '95vw' }}
                onHide={() => { if (answersModal) setAnswersModal(false); }}>
                <p style={{ marginTop: 0 }}>
                    {__('Correggi solo le risposte segnalate qui sotto. Al termine premi "Invia integrazione": il questionario viene reinviato, senza altri passaggi.', 'htr')}
                </p>
                {flaggedAnswers.map((r, i) => (
                    <div key={r.fieldId} className="appPageSection__withBorder" style={{ padding: '1rem', marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 600, marginBottom: '.35rem' }}>{i + 1}. {r.label}</div>
                        {r.motivo
                            ? <div className="appPageSection__message warning" style={{ marginBottom: '.6rem' }}>
                                <span>{__('Motivo:', 'htr')} {r.motivo}</span>
                            </div>
                            : null}
                        <InputTextarea
                            value={answerValues[r.fieldId] || ''}
                            onChange={(e) => setAnswerValues({ ...answerValues, [r.fieldId]: e.target.value })}
                            rows={4}
                            autoResize
                            style={{ width: '100%' }}/>
                    </div>
                ))}
                <div className="appPageSection__actions" style={{ marginTop: '.5rem' }}>
                    <Button type="button" outlined label={__('Annulla', 'htr')} onClick={() => setAnswersModal(false)}/>
                    <Button
                        type="button"
                        loading={submittingIntegration}
                        disabled={submittingIntegration}
                        onClick={inviaIntegrazione}
                        label={__('Invia integrazione', 'htr')}
                        icon="pi pi-check"
                        iconPos="right"/>
                </div>
            </Dialog>

            <Dialog
                header={__('Invia documenti', 'htr')}
                visible={isVisibleEmailDialog}
                style={{ width: '50vw' }}
                onHide={() => {
                    if (!isVisibleEmailDialog) return;
                    setIsVisibleEmailDialog(false);
                }}>
                <p className="m-0">
                    {data.callEmail}
                </p>
            </Dialog>
        </div>
    )

}

export default SoccorsoEditBeneficiario;
